import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Send, MessageSquare, PenSquare, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function timeAgo(iso) {
    try {
        const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return "now";
        if (s < 3600) return `${Math.floor(s / 60)}m`;
        if (s < 86400) return `${Math.floor(s / 3600)}h`;
        return `${Math.floor(s / 86400)}d`;
    } catch { return ""; }
}

const Avatar = ({ u, size = "h-9 w-9" }) => (
    u?.photo
        ? <img src={u.photo} alt={u.username || ""} className={`${size} rounded-full object-cover shrink-0 border border-[#2a2a2a]`} />
        : <div className={`${size} rounded-full shrink-0 bg-[#2a2a2a] text-white flex items-center justify-center text-sm font-bold uppercase`}>{(u?.username || u?.name || "?").charAt(0)}</div>
);

export default function Messages() {
    const { user, refreshMessagesUnread } = useAuth();
    const [params, setParams] = useSearchParams();
    const openWith = params.get("u");
    const prefill = params.get("prefill");
    const [conversations, setConversations] = useState([]);
    const [thread, setThread] = useState(null);   // { pair, other, messages }
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const bottomRef = useRef(null);

    // ---- New-message composer (type-ahead user search) ----
    const [composeOpen, setComposeOpen] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        const term = q.trim();
        if (!composeOpen || !term) { setResults([]); setSearching(false); return undefined; }
        setSearching(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/messages/search-users", { params: { q: term } });
                setResults(data.users || []);
            } catch { setResults([]); } finally { setSearching(false); }
        }, 250);
        return () => clearTimeout(t);
    }, [q, composeOpen]);

    const loadConversations = useCallback(async () => {
        try {
            const { data } = await api.get("/messages/conversations");
            setConversations(data.conversations || []);
        } catch { /* ignore */ } finally { setLoading(false); }
    }, []);

    const loadThread = useCallback(async (uid) => {
        try {
            const { data } = await api.get(`/messages/with/${uid}`);
            setThread(data);
            refreshMessagesUnread();
        } catch (e) {
            toast.error("Could not open conversation");
            setThread(null);
        }
    }, [refreshMessagesUnread]);

    // List when no user selected; thread (with polling) when one is.
    useEffect(() => {
        if (openWith) {
            loadThread(openWith);
            const t = setInterval(() => loadThread(openWith), 10000);
            return () => clearInterval(t);
        }
        setThread(null);
        loadConversations();
        return undefined;
    }, [openWith, loadThread, loadConversations]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ block: "end" }); }, [thread?.messages?.length]);

    // Prefill the composer from a ?prefill= param (e.g. "Message" link from a profile),
    // then strip it from the URL so it doesn't re-apply on refresh.
    useEffect(() => {
        if (prefill) {
            setText(prefill);
            setParams((p) => { p.delete("prefill"); return p; }, { replace: true });
        }
    }, [prefill]);  // eslint-disable-line react-hooks/exhaustive-deps

    const openConversation = (uid) => setParams((p) => { p.set("tab", "messages"); p.set("u", uid); return p; }, { replace: false });
    const backToList = () => setParams((p) => { p.set("tab", "messages"); p.delete("u"); return p; }, { replace: false });

    const send = async (e) => {
        e.preventDefault();
        const body = text.trim();
        if (!body || !thread) return;
        setSending(true);
        try {
            const { data } = await api.post("/messages", { to_user: thread.other.id, text: body });
            setThread((t) => ({ ...t, messages: [...(t?.messages || []), data] }));
            setText("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send message");
        } finally { setSending(false); }
    };

    // ---- Thread view ----
    if (openWith && thread) {
        return (
            <div data-testid="messages-thread" className="border border-[#2a2a2a] bg-[#0f0f0f]">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#2a2a2a] sticky top-0 bg-[#141414] z-10">
                    <button type="button" data-testid="messages-back" onClick={backToList} className="text-[#a0a0a0] hover:text-white"><ArrowLeft className="h-5 w-5" /></button>
                    <Link
                        to={`/u/${thread.other.username}`}
                        data-testid="messages-thread-profile-link"
                        className="flex items-center gap-3 min-w-0 group"
                        title={`View @${thread.other.username}'s profile`}
                    >
                        <Avatar u={thread.other} size="h-8 w-8" />
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-white truncate group-hover:text-[#ff4500] transition-colors">{thread.other.username}</div>
                        </div>
                    </Link>
                </div>
                <div className="px-4 py-4 space-y-2 max-h-[52vh] overflow-y-auto">
                    {thread.messages.length === 0 && <div className="text-center text-[#6a6a6a] text-sm py-8">No messages yet — say hello 👋</div>}
                    {thread.messages.map((m) => {
                        const mine = m.from_user === user.id;
                        return (
                            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="message-bubble">
                                <div className={`max-w-[78%] px-3 py-1 text-sm ${mine ? "text-[#a0a0a0]" : "text-white"}`}>
                                    <span className="whitespace-pre-wrap break-words align-baseline">{m.text}</span>
                                    <span className="text-[10px] ml-2 align-baseline whitespace-nowrap text-[#6a6a6a]">{timeAgo(m.created_at)}</span>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={bottomRef} />
                </div>
                <form onSubmit={send} className="flex items-center gap-2 px-3 py-3 border-t border-[#2a2a2a]">
                    {thread.blocked ? (
                        <div data-testid="message-blocked-note" className="w-full text-center text-xs text-[#8a8a8a] py-1">
                            {thread.blocked_by_me
                                ? "You blocked this member. Unblock them from their menu to message again."
                                : "You can't message this member."}
                        </div>
                    ) : (
                        <>
                            <input
                                data-testid="message-input"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Write a message…"
                                className="flex-1 h-10 bg-[#141414] border border-[#2a2a2a] focus:border-[#ff4500] rounded-none px-3 text-sm outline-none placeholder:text-[#666]"
                            />
                            <button type="submit" data-testid="message-send" disabled={sending || !text.trim()} className="h-10 px-3 bg-[#ff4500] text-white inline-flex items-center gap-1.5 text-sm font-semibold disabled:opacity-50">
                                <Send className="h-4 w-4" /> Send
                            </button>
                        </>
                    )}
                </form>
            </div>
        );
    }

    // ---- Conversation list ----
    const startWith = (uid) => { setComposeOpen(false); setQ(""); setResults([]); openConversation(uid); };
    return (
        <div data-testid="messages-list">
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Conversations</span>
                <button
                    type="button"
                    data-testid="new-message-btn"
                    onClick={() => { setComposeOpen((v) => !v); setQ(""); setResults([]); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#ff4500] text-white text-[11px] font-semibold uppercase tracking-[0.15em] hover:bg-[#ff5a1f] transition-colors"
                >
                    {composeOpen ? <X className="h-3.5 w-3.5" /> : <PenSquare className="h-3.5 w-3.5" />}
                    {composeOpen ? "Cancel" : "New message"}
                </button>
            </div>

            {composeOpen && (
                <div className="relative mb-4" data-testid="new-message-compose">
                    <input
                        autoFocus
                        data-testid="new-message-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Type a name or @username…"
                        className="w-full h-10 bg-[#141414] border border-[#2a2a2a] focus:border-[#ff4500] rounded-none px-3 text-sm text-white outline-none placeholder:text-[#666]"
                    />
                    {q.trim() && (
                        <div data-testid="new-message-results" className="absolute left-0 right-0 top-11 z-30 max-h-[280px] overflow-y-auto bg-[#141414] border border-[#2a2a2a] shadow-xl divide-y divide-[#2a2a2a]">
                            {searching && results.length === 0 ? (
                                <div className="px-3 py-3 text-sm text-[#6a6a6a]">Searching…</div>
                            ) : results.length === 0 ? (
                                <div className="px-3 py-3 text-sm text-[#6a6a6a]">No members found</div>
                            ) : results.map((u) => (
                                <button
                                    key={u.id}
                                    type="button"
                                    data-testid="new-message-result"
                                    onClick={() => startWith(u.id)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#1a1a1a] transition-colors"
                                >
                                    <Avatar u={u} size="h-8 w-8" />
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-white truncate">{u.username}</div>
                                        {u.name && u.name !== u.username && <div className="text-xs text-[#a0a0a0] truncate">{u.name}</div>}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {loading ? (
                <div className="text-center text-[#6a6a6a] py-10 text-sm">Loading…</div>
            ) : conversations.length === 0 ? (
                <div className="text-center text-[#6a6a6a] py-12 text-sm flex flex-col items-center gap-2">
                    <MessageSquare className="h-8 w-8 opacity-40" />
                    No conversations yet. Open a member's menu and tap Message to start one.
                </div>
            ) : (
                <div className="border border-[#2a2a2a] divide-y divide-[#2a2a2a]">
                    {conversations.map((c) => (
                        <div
                            key={c.pair}
                            data-testid="conversation-row"
                            role="button"
                            tabIndex={0}
                            onClick={() => openConversation(c.other.id)}
                            onKeyDown={(e) => { if (e.key === "Enter") openConversation(c.other.id); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#141414] transition-colors cursor-pointer"
                        >
                            <Link to={`/u/${c.other.username}`} data-testid="conversation-avatar-link" onClick={(e) => e.stopPropagation()} title={`View @${c.other.username}'s profile`} className="shrink-0">
                                <Avatar u={c.other} />
                            </Link>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                    <Link
                                        to={`/u/${c.other.username}`}
                                        data-testid="conversation-profile-link"
                                        onClick={(e) => e.stopPropagation()}
                                        title={`View @${c.other.username}'s profile`}
                                        className="text-sm font-semibold text-white truncate hover:text-[#ff4500] transition-colors"
                                    >
                                        {c.other.username}
                                    </Link>
                                    <span className="text-[10px] text-[#6a6a6a] shrink-0">{timeAgo(c.last_at)}</span>
                                </div>
                                <div className="text-xs text-[#a0a0a0] truncate">{c.last_from === user.id ? "You: " : ""}{c.last_text}</div>
                            </div>
                            {c.unread > 0 && (
                                <span data-testid="conversation-unread" className="min-w-[18px] h-[18px] px-1 bg-[#ff4500] text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0">{c.unread > 9 ? "9+" : c.unread}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
